import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Trash2 } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { CombinationPreview } from "@/components/admin/campaign-design/CombinationPreview";
import { LogoManager, type LogoDraft } from "@/components/admin/campaign-design/LogoManager";
import {
  PaletteForm,
  emptyPaletteForm,
  paletteToForm,
  type PaletteFormValues,
} from "@/components/admin/campaign-design/PaletteForm";
import {
  deleteCampaignLogo,
  deleteCampaignPalette,
  listCampaignDesign,
  saveCampaignLogo,
  saveCampaignPalette,
} from "@/lib/campaign-design.functions";
import type { DesignLogo, Palette } from "@/lib/campaign-design";

/**
 * Ticket D2: Pflege der Standarddesigns — Farbpaletten, Logos und die Vorschau
 * aller Kombinationen.
 *
 * Statt jede Kombination als eigene Datei zu produzieren, werden Logos und
 * Paletten getrennt gepflegt und zur Laufzeit zusammengesetzt. Die Auswahl durch
 * Kundinnen und Kunden folgt mit Ticket D3.
 *
 * Pflegen dürfen Administratorinnen und Editorinnen; jede Änderung wird
 * protokolliert.
 */
export const Route = createFileRoute("/_admin/admin/campaign-design")({
  head: () => ({
    meta: [
      { title: "Kampagnendesign – Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CampaignDesignPage,
});

type Tab = "palettes" | "logos" | "preview";

const TABS: { key: Tab; label: string }[] = [
  { key: "palettes", label: "Farbpaletten" },
  { key: "logos", label: "Logos" },
  { key: "preview", label: "Vorschau" },
];

function CampaignDesignPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("palettes");

  const load = useServerFn(listCampaignDesign);
  const savePalette = useServerFn(saveCampaignPalette);
  const removePalette = useServerFn(deleteCampaignPalette);
  const saveLogo = useServerFn(saveCampaignLogo);
  const removeLogo = useServerFn(deleteCampaignLogo);

  const queryKey = ["admin", "campaign-design"] as const;
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => load(),
  });

  const palettes: Palette[] = data?.palettes ?? [];
  const logos: DesignLogo[] = data?.logos ?? [];

  const [paletteForm, setPaletteForm] = useState<PaletteFormValues | null>(null);
  const [logoErrors, setLogoErrors] = useState<string[]>([]);
  const [logoNotes, setLogoNotes] = useState<string[]>([]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const paletteMutation = useMutation({
    mutationFn: async (values: PaletteFormValues) =>
      savePalette({
        data: {
          id: values.id ?? null,
          name: values.name,
          description: values.description || null,
          colorPrimary: values.colors.primary,
          colorSecondary: values.colors.secondary,
          colorText: values.colors.text,
          sortOrder: values.sortOrder,
          isActive: values.isActive,
        },
      }),
    onSuccess: async () => {
      setPaletteForm(null);
      await invalidate();
      toast.success("Palette gespeichert.");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Die Palette konnte nicht gespeichert werden."),
  });

  const paletteDelete = useMutation({
    mutationFn: async (id: string) => removePalette({ data: { id } }),
    onSuccess: async () => {
      await invalidate();
      toast.success("Palette gelöscht.");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Die Palette konnte nicht gelöscht werden."),
  });

  const logoMutation = useMutation({
    mutationFn: async (input: {
      id?: string | null;
      name: string;
      sortOrder: number;
      isActive: boolean;
      svgSource?: string | null;
    }) => saveLogo({ data: input }),
    onSuccess: async (result) => {
      if (!result.ok) {
        setLogoErrors(result.errors);
        setLogoNotes(result.notes);
        toast.error("Die Datei wurde beanstandet.");
        return;
      }
      setLogoErrors([]);
      setLogoNotes(result.notes);
      await invalidate();
      toast.success("Logo gespeichert.");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Das Logo konnte nicht gespeichert werden."),
  });

  const logoDelete = useMutation({
    mutationFn: async (id: string) => removeLogo({ data: { id } }),
    onSuccess: async () => {
      await invalidate();
      toast.success("Logo gelöscht.");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Das Logo konnte nicht gelöscht werden."),
  });

  const nextPaletteSort = palettes.reduce((max, p) => Math.max(max, p.sortOrder), 0) + 10;

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <AdminPageHeader
        title="Kampagnendesign"
        description="Fertige Logos und Farbpaletten für das Standard-Kampagnendesign. Beide werden getrennt gepflegt und beim Anzeigen zusammengesetzt: im Logo stehen reine Platzhalterfarben, die gegen die Farben der gewählten Palette ersetzt werden."
      />

      <nav aria-label="Bereiche des Kampagnendesigns" className="flex gap-1 border-b border-border">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            aria-current={tab === entry.key ? "page" : undefined}
            className={
              "-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors " +
              (tab === entry.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground")
            }
          >
            {entry.label}
          </button>
        ))}
      </nav>

      {error ? (
        <p className="text-sm text-destructive">
          Die Daten konnten nicht geladen werden: {(error as Error).message}
        </p>
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground">Daten werden geladen …</p>
      ) : tab === "palettes" ? (
        <div className="space-y-6">
          {paletteForm ? (
            <PaletteForm
              values={paletteForm}
              onChange={setPaletteForm}
              onSubmit={() => paletteMutation.mutate(paletteForm)}
              onCancel={() => setPaletteForm(null)}
              saving={paletteMutation.isPending}
            />
          ) : (
            <Button type="button" onClick={() => setPaletteForm(emptyPaletteForm(nextPaletteSort))}>
              Neue Palette
            </Button>
          )}

          {palettes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Es ist noch keine Palette hinterlegt.</p>
          ) : (
            <ul className="space-y-3">
              {palettes.map((palette) => (
                <li
                  key={palette.id}
                  className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-card p-4"
                >
                  <div className="flex gap-1" aria-hidden>
                    {[palette.colorPrimary, palette.colorSecondary, palette.colorText].map(
                      (color, i) => (
                        <span
                          key={i}
                          className="h-10 w-10 rounded-md border border-border"
                          style={{ backgroundColor: color }}
                        />
                      ),
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">
                      {palette.name}
                      {palette.isActive ? null : (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          (nicht aktiv)
                        </span>
                      )}
                    </p>
                    {palette.description ? (
                      <p className="mt-1 text-xs text-muted-foreground">{palette.description}</p>
                    ) : null}
                    <p className="mt-1 text-xs text-muted-foreground">
                      Primär {palette.colorPrimary} · Sekundär {palette.colorSecondary} · Schrift{" "}
                      {palette.colorText}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <Switch
                      checked={palette.isActive}
                      aria-label={`${palette.name} aktiv`}
                      onCheckedChange={(checked) =>
                        paletteMutation.mutate({
                          ...paletteToForm(palette),
                          isActive: checked,
                        })
                      }
                    />
                    Aktiv
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPaletteForm(paletteToForm(palette))}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    disabled={paletteDelete.isPending}
                    onClick={() => paletteDelete.mutate(palette.id)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    <span className="sr-only">{palette.name} löschen</span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : tab === "logos" ? (
        <LogoManager
          logos={logos}
          palettes={palettes}
          saving={logoMutation.isPending}

          errors={logoErrors}
          notes={logoNotes}
          onDismissMessages={() => {
            setLogoErrors([]);
            setLogoNotes([]);
          }}
          onCreate={(draft: LogoDraft) =>
            logoMutation.mutate({
              name: draft.name,
              sortOrder: draft.sortOrder,
              isActive: draft.isActive,
              svgSource: draft.svgSource,
            })
          }
          onUpdate={(logo, patch) =>
            logoMutation.mutate({
              id: logo.id,
              name: patch.name ?? logo.name,
              sortOrder: patch.sortOrder ?? logo.sortOrder,
              isActive: patch.isActive ?? logo.isActive,
              svgSource: patch.svgSource ?? null,
            })
          }
          onDelete={(logo) => logoDelete.mutate(logo.id)}
        />
      ) : (
        <CombinationPreview palettes={palettes} logos={logos} />
      )}
    </div>
  );
}
